'use client';

import {
  MutationCache,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import dynamic from 'next/dynamic';
import React from 'react';
import { CookiesProvider } from 'react-cookie';

import { ENV, IS_DEV } from '@/constants';
import { MswProvider } from '@/mocks/MswProvider';

const NavermapsProvider = dynamic(
  () =>
    import('react-naver-maps').then((mod) => ({
      default: mod.NavermapsProvider,
    })),
  { ssr: false },
);
import { GlobalController } from '@/modules';
import { useMountDeviceInfoStore } from '@/stores/DeviceStore';
import { cookies } from '@/utils/cookies';

import { ChannelTalk } from '../ChannelTalk';
import { AuthProvider } from './_components/AuthProvider';
import { GuestProvider } from './_components/GuestProvider';
import { HistoryObserver } from './_components/HistoryObserver';
import { JoinController } from './_components/JoinController';
import { PageViewEventLogger } from './_components/PageViewEventLogger';
import { useGlobalEventListener } from './_hooks/useGlobalEventListener';
import { useGtmEventClickListener } from './_hooks/useGtmEventClickListener';
import { useLoginGtmEventListener } from './_hooks/useLoginGtmEventListener';

interface GlobalProviderProps {
  children: React.ReactNode;
}

const queryClient = new QueryClient({
  mutationCache: new MutationCache({
    onSuccess: async (_data, _variables, _context, mutation) => {
      if (mutation.meta) {
        const { reset, invalidates } = mutation.meta;

        if (reset) {
          queryClient.resetQueries({
            predicate: (query) => {
              const hasIncludeQueryKey = reset.some((queryKeys) => {
                const hasAllQueryKey = queryKeys.every((queryKey) =>
                  query.queryKey.includes(queryKey),
                );

                return hasAllQueryKey;
              });

              return hasIncludeQueryKey;
            },
          });
        }
        if (invalidates) {
          await queryClient.invalidateQueries({
            predicate: (query) => {
              const hasIncludeQueryKey = invalidates.some((queryKeys) => {
                const hasAllQueryKey = queryKeys.every((queryKey) =>
                  query.queryKey.includes(queryKey),
                );

                return hasAllQueryKey;
              });

              return hasIncludeQueryKey;
            },
          });
        }
      }
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 300_000,
      retry: 2,
      retryDelay: 1_000,
    },
  },
});

export const GlobalProvider = ({ children }: GlobalProviderProps) => {
  useMountDeviceInfoStore();
  useGlobalEventListener();
  useGtmEventClickListener();
  useLoginGtmEventListener();

  return (
    <MswProvider>
      <QueryClientProvider client={queryClient}>
        <CookiesProvider
          cookies={cookies}
          defaultSetOptions={{
            path: '/',
          }}
        >
          <PageViewEventLogger />
          <HistoryObserver />
          <AuthProvider>
            <GuestProvider>
              <GlobalController />
              <JoinController />
              <NavermapsProvider
                ncpClientId={ENV.NEXT_PUBLIC_NAVER_CLOUD_CLIENT_ID}
              >
                {children}
              </NavermapsProvider>
            </GuestProvider>
          </AuthProvider>
          {IS_DEV ? (
            <ReactQueryDevtools
              buttonPosition="bottom-left"
              initialIsOpen={false}
            />
          ) : null}
          <ChannelTalk />
        </CookiesProvider>
      </QueryClientProvider>
    </MswProvider>
  );
};
